// src/Pages/PaymentLog/index.jsx

import React, { useState, useEffect } from "react";
import { Flex, Tag, Select, DatePicker, Button } from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DownloadOutlined } from "@ant-design/icons";
import DebouncedInput from "../../Component/DebouncedInput";
import CustomTable from "../../Component/Table";
import { FilterContainer } from "../../Styles/filterStyle";
import { getAllPaymentLog, exportPaymentLogs } from "../../Services/subscription";
import { formatDate } from "../../utils/date";
import { fetchUsers } from "../../Services/user_question_history";
import CustomFilterSelect from "../../Component/CustomFilterSelect";
import ExpandableText from "../../Component/ExpandText";
import ResponseViewModal from "./ResponseViewModal";
const { RangePicker } = DatePicker;

// RevenueCat webhook event types (see app/services/revenuecat.py EVENT_HANDLERS)
const EVENT_TYPE_OPTIONS = [
    "INITIAL_PURCHASE", "RENEWAL", "PRODUCT_CHANGE", "CANCELLATION",
    "UNCANCELLATION", "EXPIRATION", "BILLING_ISSUE", "TRANSFER", "REFUND",
    "SUBSCRIPTION_PAUSED", "TEMPORARY_ENTITLEMENT_GRANT", "NON_RENEWING_PURCHASE",
].map((v) => ({ label: v, value: v }));

const PROCESSING_STATUS_OPTIONS = ["Pending", "Applied", "Skipped_Duplicate", "Failed"]
    .map((v) => ({ label: v, value: v }));

const ENVIRONMENT_OPTIONS = ["PRODUCTION", "SANDBOX"].map((v) => ({ label: v, value: v }));


const PaymentLogList = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedEventType, setSelectedEventType] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedEnvironment, setSelectedEnvironment] = useState("PRODUCTION");
    const [responseModal, setResponseModal] = useState({ visible: false, values: null });

    const [dateRange, setDateRange] = useState({
        start_date: null,
        end_date: null,
    });

    const skip = (currentPage - 1) * pageSize;

    // Reset to first page when search term or selected user changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedUserId, selectedStatus, selectedEventType, selectedEnvironment, dateRange]);

    const { data, isLoading } = useQuery({
        queryKey: ["logs", currentPage, pageSize, searchTerm, selectedUserId, selectedStatus, selectedEventType, selectedEnvironment, dateRange],
        queryFn: () =>
            getAllPaymentLog({
                name: searchTerm,
                userId: selectedUserId,
                costType: selectedEventType,
                status: selectedStatus,
                environment: selectedEnvironment,
                skip,
                limit: pageSize,
                start_date: dateRange.start_date,
                end_date: dateRange.end_date,
                isCreditPlan: true,
            }),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retry: false,
    });

    // Same filters the table is showing, minus pagination.
    const { mutate: triggerDownload, isPending: isDownloading } = useMutation({
        mutationFn: async () => {
            return await exportPaymentLogs({
                name: searchTerm,
                userId: selectedUserId,
                costType: selectedEventType,
                status: selectedStatus,
                environment: selectedEnvironment,
                start_date: dateRange.start_date,
                end_date: dateRange.end_date,
            });
        },
    });

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const columns = [
        { title: "ID", dataIndex: "id", key: "id" },
        { title: "User Id", dataIndex: "user_id", key: "user_id" },
        { title: "User Name", dataIndex: "user_name", key: "user_name", render: (text) => text || "-" },
        { title: "Plan Name", dataIndex: "plan_name", key: "plan_name", render: (text) => text || "-" },
        {
            title: "Event Type",
            dataIndex: "event_type",
            key: "event_type",
            render: (text) => text || "-",
        },
        { title: "Product Id", dataIndex: "product_id", key: "product_id", render: (text) => text || "-" },
        {
            title: "Amount",
            key: "amount",
            render: (_, record) => {
                const showAmount = ["INITIAL_PURCHASE", "RENEWAL", "NON_RENEWING_PURCHASE"].includes(record.event_type);
                if (!showAmount || record.price_in_purchased_currency === null || record.price_in_purchased_currency === undefined) return "-";
                const currency = record.currency || "";
                return `${currency} ${record.price_in_purchased_currency}`.trim();
            },
        },
        {
            title: "Original Transaction Id",
            dataIndex: "original_transaction_id",
            key: "original_transaction_id",
            render: (text) => text || "-",
        },
        { title: "Environment", dataIndex: "environment", key: "environment", render: (text) => text || "-" },
        {
            title: "Processing Status",
            dataIndex: "processing_status",
            key: "processing_status",
            render: (status) => {
                const color =
                    status === "Applied" ? "green"
                    : status === "Pending" ? "orange"
                    : status === "Skipped_Duplicate" ? "blue"
                    : "red";
                return <Tag color={color}>{status}</Tag>;
            },
        },
        { title: "App User Id", dataIndex: "app_user_id", key: "app_user_id", render: (text) => text || "-" },
        { title: "Error Message", dataIndex: "error_message", key: "error_message", render: (text) => <ExpandableText text={text} /> },
        {
            title: "Response",
            key: "full_response",
            render: (_, record) => (
                <Button size="small" onClick={() => setResponseModal({ visible: true, values: record })}>
                    View
                </Button>
            ),
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (text) => (text ? formatDate(text) : "-"),
        },
        {
            title: "Updated At",
            dataIndex: "updated_at",
            key: "updated_at",
            render: (text) => (text ? formatDate(text) : "-"),
        },
    ];

    const handleDateChange = (dates) => {
        if (dates) {
        setDateRange({
            start_date: dates[0].format("YYYY-MM-DD"),
            end_date: dates[1].format("YYYY-MM-DD"),
        });
        } else {
        setDateRange({
            start_date: null,
            end_date: null,
        });
        }
    };

    return (
        <div>
            <Flex justify="space-between" align="center">
                <h3>Payment Logs</h3>
                <Button type="primary" icon={<DownloadOutlined />} onClick={triggerDownload} loading={isDownloading}>
                    Export
                </Button>
            </Flex>

            <br />

            <FilterContainer>
                <CustomFilterSelect
                    label="User"
                    fetchOptions={fetchUsers}
                    filterKey="user"
                    onChange={(key, value) => setSelectedUserId(value)}
                    renderOption={(option) => `#${option.value} ${option.label} (${option.email})`}
                    className="custom-user-select"
                />

                <DebouncedInput
                    placeholder="Search by Name..."
                    value={searchTerm}
                    onChange={setSearchTerm}
                    delay={1000}
                    width={190}
                />

                <Select
                    allowClear
                    placeholder="Select Environment"
                    style={{ width: 150 }}
                    value={selectedEnvironment}
                    onChange={(v) => setSelectedEnvironment(v ?? "PRODUCTION")}
                    options={ENVIRONMENT_OPTIONS}
                />

                <Select
                    allowClear
                    placeholder="Select Status"
                    style={{ width: 160 }}
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    options={PROCESSING_STATUS_OPTIONS}
                />

                <Select
                    allowClear
                    placeholder="Select Event Type"
                    style={{ width: 180 }}
                    value={selectedEventType}
                    onChange={setSelectedEventType}
                    options={EVENT_TYPE_OPTIONS}
                />

                <RangePicker onChange={handleDateChange} />
            </FilterContainer>

            <CustomTable
                columns={columns}
                data={data?.logs || []}
                loading={isLoading}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: data?.total_count || 0,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                    position: ["bottomRight"],
                }}
                onTableChange={handleTableChange}
                maxHeight="calc(100vh - 300px)"
            />

            <ResponseViewModal
                isModalVisible={responseModal.visible}
                handleCancel={() => setResponseModal({ visible: false, values: null })}
                initialValues={responseModal.values}
            />
        </div>
    );
};


export default PaymentLogList;
